import { AdminTableContainer, Button, EmptyState } from "@simplicity/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { apiFetch } from "../../api";
import type { ProjectListItem } from "../../configTypes";
import { formatAbsoluteTime, formatRelativeTime } from "../../lib/formatRelativeTime";
import { ProjectStatusBadge } from "./ProjectDetails";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** The 30 days is the platform's lookback (projects.handlers.ts); null means nothing landed in it. */
function LastDataSync({ at }: { at: string | null }) {
  if (at == null) {
    return <span className="italic">No data in 30 days</span>;
  }
  const epochMs = Date.parse(at);
  return (
    <time dateTime={at} title={formatAbsoluteTime(epochMs)}>
      {formatRelativeTime(epochMs)}
    </time>
  );
}

export function ProjectsList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void apiFetch<ProjectListItem[]>("/api/projects")
      .then(setProjects)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="empty-state">Loading projects...</div>;
  }

  if (error) {
    return <div className="empty-state text-destructive">{error}</div>;
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl text-foreground">Projects</h2>
        <Button variant="outline" onClick={() => void navigate("/config/projects/new")}>
          Create
        </Button>
      </div>
      <AdminTableContainer aria-label="Projects">
        {projects.length === 0 ? (
          <EmptyState title="No projects found" />
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Last data sync</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr
                  key={p.id}
                  className="clickable"
                  onClick={() => void navigate(`/config/projects/${p.id}`)}
                >
                  <td>
                    <span className="inline-flex items-center gap-2">
                      {p.name}
                      <ProjectStatusBadge status={p.status} />
                    </span>
                  </td>
                  <td className="col-id">{p.id}</td>
                  <td className="col-muted">
                    <LastDataSync at={p.lastDataSyncAt} />
                  </td>
                  <td className="col-muted">{formatDate(p.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </AdminTableContainer>
    </div>
  );
}
